import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useCurrenciesQuery } from '~/queries/useCurrenciesQuery.ts';
import {
  GUARANTEES_APPLICATION_AM_REVIEW_MODE,
  GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM,
  GUARANTEES_APPLICATION_MANUAL_TASK_QUERY_PARAM,
  GUARANTEES_APPLICATION_REVIEW_MODE_QUERY_PARAM,
  guaranteesClientText,
} from '~/modules/guarantees/form/client/constants.ts';
import {
  fetchGuaranteesApplication,
  fetchGuaranteesApplicationTenderInfo,
  fetchGuaranteesMainDeals,
  fetchGuaranteesProzorroTenderById,
} from '~/store/guarantees/thunks/applicationThunks.ts';
import { fetchGuaranteesApplicationFileCapabilities } from '~/store/guarantees/thunks/applicationFileThunks.ts';
import type { AppDispatch } from '~/store/store.ts';
import { useGuaranteesApplicationPageState } from '../../hooks/useGuaranteesApplicationPageState.ts';
import { useGuaranteesApplicationRequest } from '../../hooks/useGuaranteesApplicationRequest.ts';
import {
  buildGuaranteesCurrencyOptions,
  getGuaranteesRootTenderPublicId,
  isGuaranteeOperationTypeProzorro,
} from '../../rootSection/utils.ts';
import { useApplicationFormBootstrap } from '../../shared/hooks/useApplicationFormBootstrap.ts';
import { useApplicationRequestFeedback } from '../../shared/hooks/useApplicationRequestFeedback.ts';
import { useApplicationRequestActions } from '../../shared/hooks/useApplicationRequestActions.ts';
import { useFreshGuaranteesRequestError } from '../../shared/hooks/useFreshGuaranteesRequestError.ts';
import type { TManagerApplicationPageState } from './types.ts';
import {
  buildGuaranteesApplicationStep2Path,
  isManualAmApprovalLetterInProgress,
} from '~/modules/guarantees/utils.ts';

export const useManagerApplicationPage = (): TManagerApplicationPageState => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId = '' } = useParams();
  const routeContragentId =
    new URLSearchParams(location.search).get(GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM) ?? '';
  const isAmReviewMode =
    new URLSearchParams(location.search).get(GUARANTEES_APPLICATION_REVIEW_MODE_QUERY_PARAM) ===
    GUARANTEES_APPLICATION_AM_REVIEW_MODE;
  const isManualTaskMode =
    new URLSearchParams(location.search).get(GUARANTEES_APPLICATION_MANUAL_TASK_QUERY_PARAM) ===
    'true';
  const getStartedPath = '/guarantees/administrator/get-started';
  const [isSecretSavedForCurrentApplication, setIsSecretSavedForCurrentApplication] =
    useState(false);
  const loadedProzorroTenderBootstrapKeyRef = useRef('');
  const {
    currencies,
    isLoading: isLoadingCurrencies,
  } = useCurrenciesQuery();
  const guaranteeCurrencyOptions = useMemo(
    () => buildGuaranteesCurrencyOptions(currencies),
    [currencies]
  );

  const {
    application,
    applicationTenderInfo,
    principal,
    beneficiary,
    baseRelations,
    guaranteeForm,
    prozorroTender,
    mainDeals,
    accounts,
    principalDebitAccounts,
    securityCurrencies,
    requestError,
    isApplicationLoading,
    isCheckingApplication,
    isSavingRootSection,
    isPrincipalLoading,
    isSavingPrincipal,
    isBeneficiaryLoading,
    isSavingBeneficiary,
    isBaseRelationsLoading,
    isSavingBaseRelations,
    isGuaranteeFormLoading,
    isSavingGuaranteeForm,
    isSearchingTender,
    isSelectingTender,
    isSelectingMainDeal,
    isSavingSecret,
    isLoadingMainDeals,
    isLoadingAccounts,
    isLoadingPrincipalDebitAccounts,
    isLoadingSecurityCurrencies,
  } = useGuaranteesApplicationPageState();
  const handleApplicationChange = useCallback(() => {
    setIsSecretSavedForCurrentApplication(false);
  }, []);
  const {
    applicationNumber,
    applicationStatusConfig,
    contragentId,
    currentApplicationVersion,
    currentRouteApplication,
    effectiveApplicationId,
    formDefaultValues,
    formMethods,
    primaryBeneficiary,
    savedGuaranteeOperationType,
    shouldShowDependentSections,
    shouldSkipNextAccountRestrictionCheckRef,
    storedAccountSelection,
    storedContractSelection,
  } = useApplicationFormBootstrap({
    accounts,
    application,
    applicationId,
    beneficiaries: beneficiary,
    guaranteeCurrencyOptions,
    principalDebitAccounts,
    securityCurrencies,
    isSecretSavedForCurrentApplication,
    routeContragentId,
    onApplicationChange: handleApplicationChange,
  });
  const {
    modalAlert,
    modalSuccess,
    setModalAlert,
    setModalSuccess,
    showGuaranteesRequestError,
    handleCloseAlertModal,
    handleCloseSuccessModal,
  } = useApplicationRequestFeedback({
    applicationId,
    contragentId,
    dispatch,
  });
  const runGuaranteesApplicationRequest = useGuaranteesApplicationRequest({
    contragentId,
    applicationId: effectiveApplicationId,
    version: currentApplicationVersion,
    onError: showGuaranteesRequestError,
  });
  const navigateBackToApplications = useCallback(() => {
    navigate('/tasks');
  }, [navigate]);
  const handleSecretSectionSaveSuccess = useCallback(async () => {
    await runGuaranteesApplicationRequest({
      request: ({ applicationId: savedApplicationId }) =>
        dispatch(
          fetchGuaranteesApplicationFileCapabilities({ applicationId: savedApplicationId })
        ).unwrap(),
    });
  }, [dispatch, runGuaranteesApplicationRequest]);
  const handleCheckApplicationSuccess = useCallback(() => {
    navigate(
      buildGuaranteesApplicationStep2Path(
        effectiveApplicationId || applicationId,
        contragentId,
        true
      ),
      { replace: true }
    );
  }, [applicationId, contragentId, effectiveApplicationId, navigate]);
  const actions = useApplicationRequestActions({
    applicationId: effectiveApplicationId,
    contragentId,
    currentApplicationVersion,
    currentRouteApplication,
    formDefaultValues,
    getValues: formMethods.getValues,
    handleSubmit: formMethods.handleSubmit,
    navigateBackToApplications,
    onCheckApplicationSuccess: handleCheckApplicationSuccess,
    onSecretSectionSaveSuccess: handleSecretSectionSaveSuccess,
    prozorroTender,
    runGuaranteesApplicationRequest,
    setIsSecretSavedForCurrentApplication,
    setModalAlert,
    setModalSuccess,
    shouldSkipNextAccountRestrictionCheckRef,
    showGuaranteesRequestError,
    storedAccountSelection,
  });

  useEffect(() => {
    if (!contragentId || !applicationId || applicationId === 'new') {
      return;
    }

    void dispatch(fetchGuaranteesApplication({ contragentId, applicationId }));
    void dispatch(fetchGuaranteesMainDeals({ contragentId, page: 0, size: 20 }));
  }, [applicationId, contragentId, dispatch]);

  useEffect(() => {
    if (
      !contragentId ||
      !effectiveApplicationId ||
      effectiveApplicationId === 'new' ||
      !isGuaranteeOperationTypeProzorro(savedGuaranteeOperationType)
    ) {
      return;
    }

    const tenderPublicId = getGuaranteesRootTenderPublicId(currentRouteApplication);
    const bootstrapKey = `${effectiveApplicationId}:${tenderPublicId}`;

    if (loadedProzorroTenderBootstrapKeyRef.current === bootstrapKey) {
      return;
    }

    loadedProzorroTenderBootstrapKeyRef.current = bootstrapKey;

    void dispatch(
      fetchGuaranteesApplicationTenderInfo({
        contragentId,
        applicationId: effectiveApplicationId,
      })
    );

    if (tenderPublicId) {
      void dispatch(fetchGuaranteesProzorroTenderById({ tenderPublicId }));
    }
  }, [
    contragentId,
    currentRouteApplication,
    dispatch,
    effectiveApplicationId,
    savedGuaranteeOperationType,
  ]);

  useFreshGuaranteesRequestError(requestError, showGuaranteesRequestError);

  const redirectPath = !contragentId ? getStartedPath : '';
  const canEditManualAmApprovalLetterInProgress =
    isAmReviewMode && isManualAmApprovalLetterInProgress(currentRouteApplication, isManualTaskMode);

  return {
    contragentId,
    currentRouteApplication,
    effectiveApplicationId,
    formMethods,
    headerProps: {
      applicationNumber,
      applicationNumberLabel: guaranteesClientText.applicationsNumberLabel,
      applicationIdLabel: guaranteesClientText.applicationIdLabel,
      backLabel: guaranteesClientText.backToApplicationsButton,
      contragentId,
      contragentIdLabel: guaranteesClientText.contragentIdLabel,
      effectiveApplicationId,
      onBack: navigateBackToApplications,
      statusConfig: applicationStatusConfig,
    },
    isApplicationLoading,
    modalAlert,
    modalSuccess,
    onModalClose: handleCloseAlertModal,
    onSuccessModalClose: handleCloseSuccessModal,
    redirectPath,
    showGuaranteesRequestError,
    rootSectionProps: {
      application: currentRouteApplication,
      applicationTenderInfo,
      applicationId: effectiveApplicationId,
      storedContractSelection,
      accounts,
      principalDebitAccounts,
      securityCurrencies,
      mainDeals,
      principal,
      beneficiary: primaryBeneficiary,
      baseRelations,
      guaranteeForm,
      guaranteeCurrencyOptions,
      prozorroTender,
      isLoadingCurrencies,
      isLoadingSecurityCurrencies,
      isLoadingMainDeals,
      isLoadingAccounts,
      isLoadingPrincipalDebitAccounts,
      isLoadingPrincipal: isPrincipalLoading,
      isLoadingBeneficiary: isBeneficiaryLoading,
      isLoadingBaseRelations: isBaseRelationsLoading,
      isLoadingGuaranteeForm: isGuaranteeFormLoading,
      isSearchingTender,
      isSelectingTender,
      isSelectingMainDeal,
      isCheckingApplication,
      isSaving: isSavingRootSection,
      isSavingAccount: isSavingSecret,
      isSavingPrincipal,
      isSavingBeneficiary,
      isSavingBaseRelations,
      isSavingGuaranteeForm,
      isReadOnly: isAmReviewMode && !canEditManualAmApprovalLetterInProgress,
      requireChangesToEnableActions: true,
      checkApplicationButtonLabel: guaranteesClientText.nextButton,
      shouldShowDependentSections,
      savedGuaranteeOperationType,
      onCheckApplication: actions.handleCheckApplicationRequest,
      onSubmit: actions.handleRootFormSubmit,
      onAccountsLoad: actions.handleLoadAccounts,
      onPrincipalDebitAccountsLoad: actions.handleLoadPrincipalDebitAccounts,
      onSecurityCurrenciesLoad: actions.handleLoadSecurityCurrencies,
      onSecretSubmit: actions.handleSaveSecret,
      onPrincipalLoad: actions.handleLoadPrincipal,
      onPrincipalSubmit: actions.handleSavePrincipal,
      onBeneficiaryLoad: actions.handleLoadBeneficiary,
      onBeneficiarySubmit: actions.handleSaveBeneficiary,
      onBaseRelationsLoad: actions.handleLoadBaseRelations,
      onBaseRelationsSubmit: actions.handleSaveBaseRelations,
      onGuaranteeFormLoad: actions.handleLoadGuaranteeForm,
      onGuaranteeFormSubmit: actions.handleSaveGuaranteeForm,
      onTenderSearch: actions.handleSearchTender,
      onTenderSelect: actions.handleSelectTender,
      onMainDealSelect: actions.handleMainDealSelect,
    },
  };
};
